import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { LogoMark } from "./Logo";
import { useLang } from "../lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("4s_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("4s_chat_session", id);
  }
  return id;
};

export const ChatWidget = () => {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    const handler = () => setOpen(true);
    window.addEventListener("4s:open-chat", handler);
    return () => window.removeEventListener("4s:open-chat", handler);
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  const appendToken = (token) => {
    setMessages((m) => {
      const copy = [...m];
      copy[copy.length - 1] = { role: "assistant", content: copy[copy.length - 1].content + token };
      return copy;
    });
  };

  const send = async (preset) => {
    const text = (typeof preset === "string" ? preset : input).trim();
    if (!text || busy) return;
    if (typeof preset !== "string") setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    try {
      const resp = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message: text }),
      });
      if (!resp.ok || !resp.body) throw new Error("chat failed");
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          const parsed = JSON.parse(payload);
          if (parsed.error) throw new Error("stream error");
          if (parsed.token) appendToken(parsed.token);
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: t.chatError };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-24 right-4 z-50 md:bottom-6 md:right-6" data-testid="ai-chat-widget">
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.95 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="absolute bottom-[4.5rem] right-0 flex h-[60vh] max-h-[480px] w-[calc(100vw-2rem)] max-w-sm flex-col overflow-hidden rounded-[12px] border border-edge/70 bg-card shadow-2xl shadow-black/25 md:bottom-20"
            data-testid="ai-chat-panel"
          >
            <div className="flex items-center justify-between bg-[#181510] px-5 py-4">
              <div className="flex items-center gap-3">
                <LogoMark size={14} light />
                <div>
                  <p className="font-archivo text-sm font-bold text-white" data-testid="ai-chat-title">{t.chatTitle}</p>
                  <p className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="absolute h-full w-full animate-ping rounded-full bg-amber opacity-60" />
                      <span className="relative h-1.5 w-1.5 rounded-full bg-amber" />
                    </span>
                    {t.chatOnline}
                  </p>
                </div>
              </div>
              <button onClick={() => setOpen(false)} className="text-white/60 transition-colors hover:text-white" data-testid="ai-chat-close" aria-label="Sluit chat">
                <X size={16} />
              </button>
            </div>

            <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto bg-white/60 px-4 py-4" data-testid="ai-chat-messages">
              <div className="max-w-[88%] rounded-[10px] rounded-tl-sm border border-edge/60 bg-white p-3" data-testid="ai-chat-welcome">
                <p className="text-sm leading-relaxed text-cream">{t.chatWelcome}</p>
              </div>
              {messages.length === 0 && (
                <div className="flex flex-wrap gap-2" data-testid="ai-chat-suggestions">
                  {t.chatSuggestions.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => send(q)}
                      disabled={busy}
                      className="rounded-full border border-edge/70 bg-white px-3.5 py-2 text-left text-xs font-medium text-cream transition-colors duration-300 hover:border-amber hover:bg-amber/10 disabled:opacity-50"
                      data-testid={`ai-chat-suggestion-${i}`}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
              {messages.map((msg, i) =>
                msg.role === "user" ? (
                  <div key={i} className="ml-auto max-w-[88%] rounded-[10px] rounded-br-sm bg-[#181510] p-3" data-testid="ai-chat-message-user">
                    <p className="whitespace-pre-wrap text-sm leading-relaxed text-white">{msg.content}</p>
                  </div>
                ) : (
                  <div key={i} className="max-w-[88%] rounded-[10px] rounded-tl-sm border border-edge/60 bg-white p-3" data-testid="ai-chat-message-assistant">
                    {msg.content ? (
                      <p className="whitespace-pre-wrap text-sm leading-relaxed text-cream">{msg.content}</p>
                    ) : (
                      <span className="flex items-center gap-1 py-1" data-testid="ai-chat-typing">
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber [animation-delay:0.15s]" />
                        <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-amber [animation-delay:0.3s]" />
                      </span>
                    )}
                  </div>
                )
              )}
            </div>

            <div className="border-t border-edge/50 bg-white/80 p-3">
              <div className="flex items-center gap-2">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder={t.chatPlaceholder}
                  aria-label={t.chatPlaceholder}
                  className="input-field flex-1"
                  data-testid="ai-chat-input"
                />
                <motion.button
                  onClick={send}
                  disabled={busy || !input.trim()}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.93 }}
                  className="flex h-11 w-11 shrink-0 items-center justify-center rounded-[10px] bg-amber text-[#181510] transition-colors duration-300 hover:bg-amberdeep disabled:opacity-50"
                  data-testid="ai-chat-send-button"
                  aria-label="Verstuur bericht"
                >
                  <Send size={17} strokeWidth={2.5} />
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        onClick={() => setOpen((v) => !v)}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.92 }}
        className="relative flex h-14 w-14 items-center justify-center rounded-full bg-amber text-[#181510] shadow-lg shadow-amber/30 transition-colors duration-300 hover:bg-amberdeep"
        data-testid="ai-chat-toggle"
        aria-label="Open AI chat"
      >
        <span className="absolute inset-0 animate-ping rounded-full bg-amber opacity-25" aria-hidden="true" />
        {open ? <X size={24} strokeWidth={2.4} /> : <MessageCircle size={24} strokeWidth={2.4} />}
      </motion.button>
    </div>
  );
};
